import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from './PageLayout';
import Loading from './Loading';
import { requestProvider, init } from '@getalby/bitcoin-connect-react';
import { QRCodeSVG } from 'qrcode.react';

// Ensure Bitcoin Connect is initialized (in case this component is loaded directly)
init({
  appName: 'WebGems',
  showBalance: true,
  providerConfig: {
    nwc: {
      authorizationUrlOptions: {
        requestMethods: ['get_balance', 'make_invoice', 'lookup_invoice'],
      },
    },
  }
});

const BitcoinTest = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('not_created');
  const [error, setError] = useState(null);
  const [provider, setProvider] = useState(null);
  const [paymentCheckInterval, setPaymentCheckInterval] = useState(null);

  // Try to connect to the wallet when component mounts
  useEffect(() => {
    const connectWallet = async () => {
      try {
        setIsLoading(true);
        const weblnProvider = await requestProvider();
        setProvider(weblnProvider);
        console.log('Bitcoin wallet connected successfully in test component!');
        
        // Check which methods are available on the provider
        const methods = Object.keys(weblnProvider).filter(
          key => typeof weblnProvider[key] === 'function'
        );
        console.log('Available methods:', methods);
      } catch (error) {
        console.error('Error connecting to wallet:', error);
        setError('Failed to connect to wallet. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    connectWallet();

    // Clean up any intervals when the component unmounts
    return () => {
      if (paymentCheckInterval) {
        clearInterval(paymentCheckInterval);
      }
    };
  }, []);

  const createInvoice = async () => {
    if (!provider) {
      setError('Wallet not connected. Please connect a wallet first.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      // Create an invoice for 100 satoshis
      const invoiceRequest = {
        amount: 100, // 100 satoshis
        defaultMemo: 'WebGems Test Payment',
      };
      
      const invoiceResponse = await provider.makeInvoice(invoiceRequest);
      setInvoice(invoiceResponse);
      setPaymentStatus('pending');
      console.log('Invoice created:', invoiceResponse);
      
      // Start checking for payment
      startPaymentCheck(invoiceResponse.paymentRequest || invoiceResponse.payment_request);
    } catch (error) {
      console.error('Error creating invoice:', error);
      setError('Failed to create invoice. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const startPaymentCheck = (paymentRequest) => {
    // Clear any existing interval
    if (paymentCheckInterval) {
      clearInterval(paymentCheckInterval);
    }
    
    // Set up regular checks for payment
    const interval = setInterval(async () => {
      try {
        const paymentStatus = await checkPayment(paymentRequest);
        if (paymentStatus.paid) {
          clearInterval(interval);
          setPaymentCheckInterval(null);
          setPaymentStatus('paid');
          console.log('🎉 Payment received!', paymentStatus);
        }
      } catch (error) {
        console.error('Error checking payment:', error);
      }
    }, 3000); // Check every 3 seconds
    
    setPaymentCheckInterval(interval);
  };

  const checkPayment = async (paymentRequest) => {
    if (!provider || !provider.lookupInvoice) {
      throw new Error('Provider does not support lookupInvoice');
    }
    
    try {
      const invoiceStatus = await provider.lookupInvoice({ paymentRequest });
      console.log('Invoice status:', invoiceStatus);
      
      return {
        paid: invoiceStatus.paid || invoiceStatus.settled || false,
        preimage: invoiceStatus.preimage,
      };
    } catch (error) {
      console.error('Error looking up invoice:', error);
      throw error;
    }
  };

  const handleBack = () => {
    // Clean up interval before navigating away
    if (paymentCheckInterval) {
      clearInterval(paymentCheckInterval);
    }
    navigate(-1);
  };

  const getStatusDisplay = () => {
    switch (paymentStatus) {
      case 'not_created':
        return null;
      case 'pending':
        return (
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded-md mb-4">
            <p className="font-bold">Payment Pending</p>
            <p>Waiting for payment to be received...</p>
          </div>
        );
      case 'paid':
        return (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md mb-4">
            <p className="font-bold">Payment Received!</p>
            <p>Thank you for testing the Bitcoin Lightning integration.</p>
          </div>
        );
      default:
        return null;
    }
  };

  if (isLoading && !invoice) {
    return <Loading />;
  }

  return (
    <PageLayout>
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-100px)]">
        <div className="w-full max-w-md">
          <h1 className="text-2xl font-bold text-white text-center mb-6">
            Bitcoin Lightning Test
          </h1>
          
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-4">
              {error}
            </div>
          )}
          
          {getStatusDisplay()}
          
          {invoice && (
            <div className="bg-white p-4 rounded-md shadow-md mb-6">
              <h3 className="font-bold mb-2">Invoice:</h3>
              <p className="text-xs break-all bg-gray-100 p-2 rounded">
                {invoice.paymentRequest || invoice.payment_request}
              </p>
              
              {paymentStatus === 'pending' && (
                <div className="mt-4">
                  <p className="text-sm text-gray-700 mb-2">
                    Scan this QR code with a Lightning wallet to pay 100 sats
                  </p>
                  <div className="flex justify-center p-4 bg-white">
                    <QRCodeSVG
                      value={invoice.paymentRequest || invoice.payment_request}
                      size={200}
                      bgColor="#ffffff"
                      fgColor="#000000"
                      level="L"
                      includeMargin={true}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          
          <div className="flex justify-center space-x-4 mt-6">
            <button
              onClick={handleBack}
              className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition duration-300"
            >
              Back
            </button>
            
            {!invoice && (
              <button
                onClick={createInvoice}
                disabled={isLoading || !provider}
                className={`px-4 py-2 rounded-md text-white ${
                  !isLoading && provider
                    ? 'bg-blue-500 hover:bg-blue-600'
                    : 'bg-gray-400 cursor-not-allowed'
                } transition duration-300`}
              >
                {isLoading ? 'Creating...' : 'Create 100 sat Invoice'}
              </button>
            )}
            
            {invoice && paymentStatus === 'pending' && (
              <button
                onClick={() => checkPayment(invoice.paymentRequest || invoice.payment_request)}
                className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition duration-300"
              >
                Check Payment
              </button>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default BitcoinTest;
